import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { ProfileCard, ProfileCardHeader } from './ProfileCard';
import { useTerminology } from '../../hooks/useTerminology';
import { useAccount } from '../../contexts/AccountContext';
import { cgpa } from '../../data/academics';
import { transactions } from '../../data/finance';
import { deriveBalances } from '../../utils/ledger';
import { naira } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The three at-a-glance cards.
 *
 * Each reads the domain that owns the figure — academics for standing, the
 * ledger for balances, the account for campus and interests — rather than
 * keeping its own copy. Nothing here is stored: the balance is derived from
 * transactions on every read, exactly as the Money surface derives it.
 */
export function AcademicSnapshot() {
  const {
    account
  } = useAccount();
  const standing = account.academic.status;
  return <ProfileCard interactive data-id="element-4583" data-mp="2mevrftce6y1d">
      <ProfileCardHeader icon="academic" title="Academic" tone="info" data-id="element-4584" data-mp="2may8p8tq88i0" />
      <div className="flex flex-1 flex-col px-5 pb-5 pt-4" data-id="element-4585" data-mp="2hcwmwg4x1v2u">
        <p className="text-body-sm text-ink-muted" data-id="element-4586" data-mp="2ced0yfqbojah">CGPA</p>
        <div className="mt-1 flex flex-wrap items-center gap-2.5" data-id="element-4587" data-mp="2cgy4raa6l9av">
          <p className="cz-num text-display font-semibold tracking-tight text-ink" data-id="element-4588" data-mp="28f4617jjqfdc">
            {cgpa.toFixed(2)}
          </p>
          {standing && <Badge tone="ok" data-id="element-4589" data-mp="304pf48c5vc9i">{standing}</Badge>}
        </div>
        <div className="mt-4 space-y-0.5 text-body-sm text-ink-muted" data-id="element-4590" data-mp="3058y7sdjs6p4">
          <p data-id="element-4591" data-mp="2vd7exmhypfpl">{account.academic.programme ?? 'Programme not added'}</p>
          <p data-id="element-4592" data-mp="2rcduj33qaj1g">{account.academic.level ?? 'Level not added'}</p>
        </div>
        <Link to="/academics" className="mt-auto pt-4 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4593" data-mp="2rab26cf3sdtk">
          View academic →
        </Link>
      </div>
    </ProfileCard>;
}
export function CampusSnapshot() {
  const {
    phrase
  } = useTerminology();
  const {
    institutionName,
    campusName
  } = useAccount();
  const {
    identity
  } = useAuth();
  const interests = identity?.interests ?? [];
  return <ProfileCard interactive data-id="element-4594" data-mp="17grje37zlh4x">
      <ProfileCardHeader icon="campus" title="{campus}" tone="brand" data-id="element-4595" data-mp="17fgeg02t23c8" />
      <div className="flex flex-1 flex-col px-5 pb-5 pt-4" data-id="element-4596" data-mp="1ceszcfa490c6">
        <p className="text-body-lg font-semibold text-ink" data-id="element-4597" data-mp="1cd856qrprjeu">{institutionName}</p>
        <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-4598" data-mp="ydyta3ajjbgo">{campusName}</p>

        <p className="mt-4 text-body-sm text-ink-muted" data-id="element-4599" data-mp="13fo1zcaajd1l">
          {interests.length === 0 ? 'No interests added yet' : `${interests.length} interest${interests.length === 1 ? '' : 's'} added`}
        </p>
        {interests.length > 0 && <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-4600" data-mp="2ivmwhkqzjajd">
            {interests.slice(0, 4).map(interest => <li key={interest} className="rounded-full border border-line bg-canvas px-2.5 py-1 text-caption text-ink-muted" data-id="element-4601" data-mp="2nuzt2x7nrvz5">
                {interest}
              </li>)}
            {interests.length > 4 && <li className="rounded-full border border-line bg-canvas px-2.5 py-1 text-caption text-ink-muted" data-id="element-4602" data-mp="lwxoh626l8io">
                +{interests.length - 4}
              </li>}
          </ul>}

        <Link to="/social" className="mt-auto pt-4 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4603" data-mp="uxccid6j51pv">
          {phrase('Manage interests')} →
        </Link>
      </div>
    </ProfileCard>;
}
export function WalletSnapshot() {
  const [shown, setShown] = useState(false);
  const balances = deriveBalances(transactions);

  // Formatted through the platform's own currency helper, so the profile cannot
  // disagree with the Money surface about what a balance reads as.
  const formatted = naira(balances.available);
  return <ProfileCard interactive data-id="element-4604" data-mp="31pwrj7y44kvr">
      <ProfileCardHeader icon="wallet" title="Money" tone="warn" data-id="element-4605" data-mp="335hjfidvu0ql" />
      <div className="flex flex-1 flex-col px-5 pb-5 pt-4" data-id="element-4606" data-mp="334zb3ndy3w2b">
        <p className="text-body-sm text-ink-muted" data-id="element-4607" data-mp="383ao26zjemor">Available balance</p>
        <div className="mt-1 flex items-center gap-2" data-id="element-4608" data-mp="3d3olyw7fcg92">
          <p className="cz-num text-h2 font-semibold tracking-tight text-ink" data-id="element-4609" data-mp="3d46qsh85k2sl">
            {shown ? formatted : '•••••••'}
          </p>
          <button type="button" onClick={() => setShown(current => !current)} aria-label={shown ? 'Hide balance' : 'Show balance'} className="flex min-h-touch min-w-touch items-center justify-center rounded-lg text-ink-subtle transition-colors duration-150 ease-out hover:text-ink" data-id="element-4610" data-mp="2kercnil6xtsg">
            {shown ? <EyeOffIcon className="h-4 w-4" data-id="element-4611" data-mp="2z572jaojxpwy" /> : <EyeIcon className="h-4 w-4" data-id="element-4612" data-mp="21o9prun1hsf0" />}
          </button>
        </div>
        <p className="mt-4 max-w-[34ch] text-body-sm text-ink-muted" data-id="element-4613" data-mp="21o00k99tjpad">
          Derived from your ledger entries. Campuzon records these balances and does not hold your
          money.
        </p>
        <Link to="/money" className="mt-auto pt-4 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4614" data-mp="nrfej7jgeho1">
          Manage money →
        </Link>
      </div>
    </ProfileCard>;
}